import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowsClockwise,
  ArrowsOutCardinal,
  FloppyDisk,
  PencilRuler,
  Scissors,
  Sparkle,
  Trash,
  UploadSimple,
  Image as ImageIcon,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AppHeader } from "@/components/AppHeader";
import { SearchSelect } from "@/components/SearchSelect";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { shrinkForUpload } from "@/lib/shrinkImage";

const SNAP = 1;
const SCALE = 5; // px per inch at 100% (slab drawn scaled to fit)

export default function QuoteLayout() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [slabs, setSlabs] = useState([]);
  const [remnants, setRemnants] = useState([]);
  const [layout, setLayout] = useState({ slabs: [], placements: {} });
  const [active, setActive] = useState(0);
  const [drag, setDrag] = useState(null);
  const [view, setView] = useState("single");
  const [mirror, setMirror] = useState(true);
  const [tool, setTool] = useState("move");
  const [seamDir, setSeamDir] = useState("v");
  const [selSeam, setSelSeam] = useState(null);
  const seamDragRef = useRef(null);
  const [pieceMenu, setPieceMenu] = useState(null);
  const [menuAngle, setMenuAngle] = useState(90);
  const photoRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", await shrinkForUpload(file));
    try {
      const { data } = await api.post("/quotes/layout-photo", fd);
      setSlab(active, {
        photo_url: `${API}/photos/${data.storage_path}?auth=${localStorage.getItem("sw_token")}`,
      });
      toast.success("Slab photo uploaded");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not upload that photo",
      );
    }
    setUploading(false);
  };
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [q, s, r] = await Promise.all([
      api.get(`/quotes/${id}`),
      api.get("/slabs"),
      api.get("/remnants"),
    ]);
    setQuote(q.data);
    setSlabs(s.data);
    setRemnants(r.data.items || r.data || []);
    const l =
      q.data.layout && q.data.layout.slabs
        ? q.data.layout
        : { slabs: [], placements: {} };
    setLayout(l);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const pieces = useMemo(() => {
    if (!quote) return [];
    return quote.areas.flatMap((a) =>
      a.pieces.map((p) => ({
        ...p,
        area: a.name,
        sqft:
          p.shape === "poly" && p.points.length > 2
            ? Math.abs(
                p.points.reduce((s, [x, y], i) => {
                  const [x2, y2] = p.points[(i + 1) % p.points.length];
                  return s + x * y2 - x2 * y;
                }, 0) / 2,
              ) / 144
            : (p.length_in * p.width_in) / 144,
      })),
    );
  }, [quote]);

  const stockOptions = useMemo(
    () => [
      ...slabs.map((s) => ({
        value: s.id,
        label: `${s.code || "Slab"} · ${s.slab_color} · ${Math.round(
          Number(s.length_in) || 0,
        )}" x ${Math.round(Number(s.width_in) || 0)}"${
          (s.photos || []).some((p) => !p.is_deleted) ? " · photo" : ""
        }`,
      })),
      ...remnants.map((r) => ({
        value: r.id,
        label: `${r.code || "Remnant"} · ${r.slab_color} · ${Math.round(
          Number(r.length_in) || 0,
        )}" x ${Math.round(Number(r.width_in) || 0)}" · remnant`,
      })),
    ],
    [slabs, remnants],
  );

  const addSlab = () => {
    setLayout((l) => ({
      ...l,
      slabs: [
        ...l.slabs,
        {
          id: Math.random().toString(36).slice(2, 9),
          slab_id: "",
          photo_url: "",
          length_in: 126,
          width_in: 63,
        },
      ],
    }));
    setActive(layout.slabs.length);
  };

  const pickStock = (value) => {
    if (!value) {
      setSlab(active, { slab_id: "" });
      return;
    }
    const rec =
      slabs.find((s) => s.id === value) || remnants.find((r) => r.id === value);
    if (!rec) return;
    const photo = (rec.photos || []).find((p) => !p.is_deleted);
    setSlab(active, {
      slab_id: value,
      material: rec.slab_color || "",
      length_in: Number(rec.length_in) || slab.length_in,
      width_in: Number(rec.width_in) || slab.width_in,
      ...(photo
        ? {
            photo_url: `${API}/photos/${photo.storage_path}?auth=${localStorage.getItem("sw_token")}`,
            photo_zoom: 1,
            photo_x: 0,
            photo_y: 0,
          }
        : {}),
    });
  };

  const removeSlab = () => {
    const gone = layout.slabs[active];
    if (!gone || layout.slabs.length < 2) return;
    setLayout((l) => {
      const placements = Object.fromEntries(
        Object.entries(l.placements || {})
          .filter(([, pl]) => pl.slab !== active)
          .map(([id, pl]) => [
            id,
            pl.slab > active ? { ...pl, slab: pl.slab - 1 } : pl,
          ]),
      );
      return {
        ...l,
        slabs: l.slabs.filter((_, j) => j !== active),
        placements,
        cuts: (l.cuts || [])
          .filter((c) => c.slab !== active)
          .map((c) => (c.slab > active ? { ...c, slab: c.slab - 1 } : c)),
      };
    });
    setActive((a) => Math.max(0, a - 1));
    toast.success("Slab removed");
  };

  const setSlab = (i, patch) =>
    setLayout((l) => ({
      ...l,
      slabs: l.slabs.map((s, j) => (j === i ? { ...s, ...patch } : s)),
    }));

  const place = (pieceId, slabIndex) =>
    setLayout((l) => ({
      ...l,
      placements: {
        ...l.placements,
        [pieceId]: { slab: slabIndex, x: 2, y: 2, rot: 0 },
      },
    }));

  const unplace = (pieceId) =>
    setLayout((l) => {
      const p = { ...l.placements };
      delete p[pieceId];
      return { ...l, placements: p };
    });

  const rotate = (pieceId, deg = 90) =>
    setLayout((l) => ({
      ...l,
      placements: {
        ...l.placements,
        [pieceId]: {
          ...l.placements[pieceId],
          rot: ((((l.placements[pieceId].rot || 0) + deg) % 360) + 360) % 360,
        },
      },
    }));

  const moveToSlab = (pieceId, slabIndex) => {
    setLayout((l) => ({
      ...l,
      placements: {
        ...l.placements,
        [pieceId]: { ...l.placements[pieceId], slab: slabIndex, x: 2, y: 2 },
      },
    }));
    setActive(slabIndex);
    setPieceMenu(null);
  };

  const autoNest = () => {
    const slab = layout.slabs[active];
    if (!slab) return;
    let x = 2;
    let y = 2;
    let rowH = 0;
    const next = { ...layout.placements };
    pieces
      .slice()
      .sort((a, b) => b.sqft - a.sqft)
      .forEach((p) => {
        const w = p.length_in || 1;
        const h = p.width_in || 1;
        if (x + w > slab.length_in - 2) {
          x = 2;
          y += rowH + 2;
          rowH = 0;
        }
        if (y + h > slab.width_in - 2) return;
        next[p.id] = {
          slab: active,
          x: Math.round(x),
          y: Math.round(y),
          rot: 0,
        };
        x += w + 2;
        rowH = Math.max(rowH, h);
      });
    setLayout({ ...layout, placements: next });
    toast.success("Auto-nest placed the pieces — drag to fine-tune the vein");
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/quotes/${id}/layout`, layout);
      setQuote(data);
      toast.success("Layout saved");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save layout",
      );
    }
    setBusy(false);
  };

  const seams = layout.cuts || [];

  const addSeam = (e, slabIndex) => {
    setPieceMenu(null);
    if (tool !== "seam") return;
    const s = layout.slabs[slabIndex];
    const rect = e.currentTarget.getBoundingClientRect();
    const pxPerIn = rect.width / s.length_in;
    const at =
      seamDir === "v"
        ? (e.clientX - rect.left) / pxPerIn
        : (e.clientY - rect.top) / pxPerIn;
    setLayout((l) => ({
      ...l,
      cuts: [
        ...(l.cuts || []),
        {
          id: Math.random().toString(36).slice(2, 9),
          slab: slabIndex,
          dir: seamDir,
          at: Math.round(at * 2) / 2,
        },
      ],
    }));
  };

  const dropSeam = (seamId) => {
    setSelSeam((cur) => (cur === seamId ? null : cur));
    setLayout((l) => ({
      ...l,
      cuts: (l.cuts || []).filter((c) => c.id !== seamId),
    }));
  };

  const setSeamAt = (seamId, at) =>
    setLayout((l) => ({
      ...l,
      cuts: (l.cuts || []).map((c) =>
        c.id === seamId ? { ...c, at: Math.round(Number(at) * 2) / 2 } : c,
      ),
    }));

  const grabSeam = (e, c) => {
    e.stopPropagation();
    setSelSeam(c.id);
    seamDragRef.current = { id: c.id };
  };

  const moveSeam = (e, slabIndex) => {
    const drag = seamDragRef.current;
    if (!drag) return;
    const c = (layout.cuts || []).find((x) => x.id === drag.id);
    if (!c) return;
    const s = layout.slabs[slabIndex];
    const rect = e.currentTarget.getBoundingClientRect();
    const pxPerIn = rect.width / s.length_in;
    const raw =
      c.dir === "v"
        ? (e.clientX - rect.left) / pxPerIn
        : (e.clientY - rect.top) / pxPerIn;
    const max = c.dir === "v" ? s.length_in : s.width_in;
    setSeamAt(c.id, Math.min(Math.max(raw, 0), max));
  };

  // seams only run across the drawing, not the whole slab
  const openPieceMenu = (e, pieceId, slabIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const svg = e.currentTarget.closest("svg");
    const host = svg?.parentElement;
    const box = host?.getBoundingClientRect();
    const s = layout.slabs[slabIndex];
    const rect = svg.getBoundingClientRect();
    const pxPerIn = rect.width / s.length_in;
    setActive(slabIndex);
    setPieceMenu({
      pieceId,
      slabIndex,
      atX: Math.round(((e.clientX - rect.left) / pxPerIn) * 2) / 2,
      atY: Math.round(((e.clientY - rect.top) / pxPerIn) * 2) / 2,
      left: box ? e.clientX - box.left : 0,
      top: box ? e.clientY - box.top : 0,
    });
  };

  const seamAtMenu = (dir) => {
    if (!pieceMenu) return;
    setLayout((l) => ({
      ...l,
      cuts: [
        ...(l.cuts || []),
        {
          id: Math.random().toString(36).slice(2, 9),
          slab: pieceMenu.slabIndex,
          dir,
          at: dir === "v" ? pieceMenu.atX : pieceMenu.atY,
        },
      ],
    }));
    setPieceMenu(null);
  };

  // floating toolbar shown on right-click, over whichever slab the piece sits on
  const renderPieceMenu = (index) => {
    if (!pieceMenu || pieceMenu.slabIndex !== index) return null;
    const mp = pieces.find((p) => p.id === pieceMenu.pieceId);
    if (!mp) return null;
    return (
      <div
        data-testid="layout-piece-menu"
        style={{ left: pieceMenu.left, top: pieceMenu.top }}
        className="absolute z-30 flex max-w-[94%] -translate-x-1/2 flex-wrap items-center justify-center gap-1.5 rounded-xl border border-zinc-700 bg-zinc-900/95 p-1.5 shadow-2xl backdrop-blur"
        onPointerDown={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      >
        <div className="rounded-lg bg-zinc-800 px-2.5 py-1 text-center">
          <span className="block text-[8px] font-bold uppercase tracking-widest text-zinc-500">
            Area
          </span>
          <span
            className="font-mono-data block text-[11px] font-bold text-zinc-100"
            data-testid="menu-sqft"
          >
            {mp.sqft.toFixed(2)} SF
          </span>
        </div>
        <button
          type="button"
          data-testid="menu-rotate-left"
          title="Rotate anti-clockwise"
          onClick={() => rotate(pieceMenu.pieceId, -menuAngle)}
          className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
        >
          <ArrowCounterClockwise size={14} weight="bold" />
        </button>
        <Select
          value={String(menuAngle)}
          onValueChange={(v) => setMenuAngle(Number(v))}
        >
          <SelectTrigger
            data-testid="menu-angle"
            className="h-9 w-24 border-zinc-700 bg-zinc-950 text-xs"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[1, 5, 15, 22.5, 45, 90].map((a) => (
              <SelectItem key={a} value={String(a)}>
                {a}°
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          data-testid="menu-rotate"
          title="Rotate clockwise"
          onClick={() => rotate(pieceMenu.pieceId, menuAngle)}
          className="rounded-lg border border-zinc-700 p-2 text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
        >
          <ArrowClockwise size={14} weight="bold" />
        </button>
        {layout.slabs.length > 1 && (
          <Select
            value={String(pieceMenu.slabIndex)}
            onValueChange={(v) => moveToSlab(pieceMenu.pieceId, Number(v))}
          >
            <SelectTrigger
              data-testid="menu-slab"
              className="h-9 w-36 border-zinc-700 bg-zinc-950 text-xs"
            >
              <SelectValue placeholder="Slab" />
            </SelectTrigger>
            <SelectContent>
              {layout.slabs.map((sl, si) => (
                <SelectItem key={sl.id || si} value={String(si)}>
                  {`Slab ${si + 1} · ${sl.material || "Unassigned"}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <button
          type="button"
          data-testid="menu-seam-v"
          title="Add a seam down the piece"
          onClick={() => seamAtMenu("v")}
          className="flex items-center gap-1 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
        >
          <Scissors size={14} weight="bold" />
          <span className="text-[10px] font-bold">↕</span>
        </button>
        <button
          type="button"
          data-testid="menu-seam-h"
          title="Add a seam across the piece"
          onClick={() => seamAtMenu("h")}
          className="flex items-center gap-1 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-amber-200 transition-colors duration-150 hover:bg-amber-500/20"
        >
          <Scissors size={14} weight="bold" />
          <span className="text-[10px] font-bold">↔</span>
        </button>
        <button
          type="button"
          data-testid="menu-remove"
          title="Remove from the slab"
          onClick={() => {
            unplace(pieceMenu.pieceId);
            setPieceMenu(null);
          }}
          className="rounded-lg border border-zinc-700 p-2 text-red-400 transition-colors duration-150 hover:bg-red-500/10"
        >
          <Trash size={14} weight="bold" />
        </button>
      </div>
    );
  };

  const drawBounds = (index) => {
    const s = layout.slabs[index];
    const placed = pieces.filter(
      (p) => layout.placements[p.id]?.slab === index,
    );
    if (!placed.length)
      return { x0: 0, y0: 0, x1: s.length_in, y1: s.width_in };
    let x0 = Infinity;
    let y0 = Infinity;
    let x1 = -Infinity;
    let y1 = -Infinity;
    placed.forEach((p) => {
      const pl = layout.placements[p.id];
      const swap = (((pl.rot || 0) / 90) | 0) % 2 === 1;
      const w = swap ? p.width_in : p.length_in;
      const h = swap ? p.length_in : p.width_in;
      x0 = Math.min(x0, pl.x);
      y0 = Math.min(y0, pl.y);
      x1 = Math.max(x1, pl.x + w);
      y1 = Math.max(y1, pl.y + h);
    });
    return { x0, y0, x1, y1 };
  };

  const photoFor = (slab) => {
    const token = localStorage.getItem("sw_token");
    // re-sign any stored URL so an old baked-in auth token can't 401
    if (slab.photo_url) return `${slab.photo_url.split("?")[0]}?auth=${token}`;
    const rec = slabs.find((s) => s.id === slab.slab_id);
    const photo = (rec?.photos || []).find((p) => !p.is_deleted);
    return photo
      ? `${API}/photos/${photo.storage_path}?auth=${token}`
      : "";
  };

  const onPointerDown = (e, pieceId, slabIndex) => {
    if (tool === "seam") return; // let the slab handle the seam click
    e.preventDefault();
    const rect = e.currentTarget.closest("svg").getBoundingClientRect();
    setDrag({ pieceId, rect, slabIndex });
  };

  const onPointerMove = (e) => {
    if (!drag) return;
    const slab = layout.slabs[drag.slabIndex];
    if (!slab) return;
    const pxPerIn = drag.rect.width / slab.length_in;
    const x = Math.max(
      0,
      Math.round((e.clientX - drag.rect.left) / pxPerIn / SNAP) * SNAP,
    );
    const y = Math.max(
      0,
      Math.round((e.clientY - drag.rect.top) / pxPerIn / SNAP) * SNAP,
    );
    setLayout((l) => ({
      ...l,
      placements: {
        ...l.placements,
        [drag.pieceId]: { ...l.placements[drag.pieceId], x, y },
      },
    }));
  };

  // dragging onto another slab in the all-slabs view moves the piece there
  const dragOntoSlab = (e, slabIndex) => {
    if (!drag) return;
    e.stopPropagation();
    const svg = e.currentTarget.querySelector("svg");
    const s = layout.slabs[slabIndex];
    if (!svg || !s) return;
    const rect = svg.getBoundingClientRect();
    const pxPerIn = rect.width / s.length_in;
    const x = Math.max(
      0,
      Math.round((e.clientX - rect.left) / pxPerIn / SNAP) * SNAP,
    );
    const y = Math.max(
      0,
      Math.round((e.clientY - rect.top) / pxPerIn / SNAP) * SNAP,
    );
    setLayout((l) => ({
      ...l,
      placements: {
        ...l.placements,
        [drag.pieceId]: {
          ...l.placements[drag.pieceId],
          slab: slabIndex,
          x,
          y,
        },
      },
    }));
    if (drag.slabIndex !== slabIndex) {
      setDrag({ ...drag, slabIndex, rect });
      setActive(slabIndex);
    }
  };

  if (!quote)
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Slab layout" />
      </div>
    );

  const slab = layout.slabs[active];
  const placedOnActive = pieces.filter(
    (p) => layout.placements[p.id]?.slab === active,
  );
  const usedSqft = placedOnActive.reduce((n, p) => n + p.sqft, 0);
  const slabSqft = slab ? (slab.length_in * slab.width_in) / 144 : 0;
  const yieldPct = slabSqft ? Math.round((usedSqft / slabSqft) * 100) : 0;
  const pair =
    layout.slabs.length > 1
      ? [
          active,
          active + 1 < layout.slabs.length ? active + 1 : active - 1,
        ].sort((a, b) => a - b)
      : [];

  const SlabCanvas = ({ index, mirrored = false, testid }) => {
    const s = layout.slabs[index];
    const placed = pieces.filter(
      (p) => layout.placements[p.id]?.slab === index,
    );
    const photo = photoFor(s);
    return (
      <svg
        data-testid={testid}
        viewBox={`0 0 ${s.length_in} ${s.width_in}`}
        className={`w-full touch-none ${tool === "seam" ? "cursor-crosshair" : ""}`}
        style={{ aspectRatio: `${s.length_in} / ${s.width_in}` }}
        onPointerDown={(e) => addSeam(e, index)}
        onPointerMove={(e) => moveSeam(e, index)}
        onPointerUp={() => {
          seamDragRef.current = null;
        }}
        onPointerLeave={() => {
          seamDragRef.current = null;
        }}
      >
        {photo ? (
          <image
            href={photo}
            x="0"
            y="0"
            width={s.length_in}
            height={s.width_in}
            preserveAspectRatio="xMidYMid slice"
            transform={
              mirrored ? `translate(${s.length_in} 0) scale(-1 1)` : undefined
            }
          />
        ) : (
          <rect width={s.length_in} height={s.width_in} fill="#18181b" />
        )}
        {placed.map((p) => {
          const pl = layout.placements[p.id];
          const w = p.length_in || 1;
          const h = p.width_in || 1;
          return (
            <g
              key={p.id}
              data-testid={`layout-piece-${p.id}`}
              transform={`translate(${pl.x} ${pl.y}) rotate(${pl.rot} ${w / 2} ${h / 2})`}
              onPointerDown={(e) => onPointerDown(e, p.id, index)}
              onDoubleClick={() => rotate(p.id)}
              onContextMenu={(e) => openPieceMenu(e, p.id, index)}
              className={tool === "seam" ? "cursor-crosshair" : "cursor-move"}
            >
              {p.shape === "poly" && p.points.length > 2 ? (
                <polygon
                  points={p.points.map(([x, y]) => `${x},${y}`).join(" ")}
                  fill="rgba(99,102,241,0.35)"
                  stroke="#a5b4fc"
                  strokeWidth="0.6"
                />
              ) : (
                <rect
                  width={w}
                  height={h}
                  fill="rgba(99,102,241,0.35)"
                  stroke="#a5b4fc"
                  strokeWidth="0.6"
                />
              )}
              <text x="2" y="6" fill="#e4e4e7" fontSize="4">
                {p.label}
              </text>
            </g>
          );
        })}
        {seams
          .filter((c) => c.slab === index)
          .map((c) => {
            const b = drawBounds(index);
            const pad = 1.5;
            const x1 = c.dir === "v" ? c.at : Math.max(0, b.x0 - pad);
            const x2 = c.dir === "v" ? c.at : Math.min(s.length_in, b.x1 + pad);
            const y1 = c.dir === "v" ? Math.max(0, b.y0 - pad) : c.at;
            const y2 = c.dir === "v" ? Math.min(s.width_in, b.y1 + pad) : c.at;
            return (
              <g key={c.id} data-testid={`layout-seam-${c.id}`}>
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={selSeam === c.id ? "#fde68a" : "#f59e0b"}
                  strokeWidth={selSeam === c.id ? 1.1 : 0.7}
                  strokeDasharray="3 2"
                />
                <line
                  data-testid={`layout-seam-hit-${c.id}`}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke="transparent"
                  strokeWidth="4"
                  className="cursor-move"
                  onPointerDown={(e) => grabSeam(e, c)}
                />
                <circle
                  data-testid={`layout-seam-handle-${c.id}`}
                  cx={c.dir === "v" ? c.at : (x1 + x2) / 2}
                  cy={c.dir === "v" ? (y1 + y2) / 2 : c.at}
                  r="1.5"
                  fill={selSeam === c.id ? "#fde68a" : "#f59e0b"}
                  stroke="#09090b"
                  strokeWidth="0.3"
                  className="cursor-grab"
                  onPointerDown={(e) => grabSeam(e, c)}
                />
                <text
                  x={c.dir === "v" ? c.at + 1 : x1 + 1}
                  y={c.dir === "v" ? y1 + 2.4 : c.at - 1}
                  fill="#fbbf24"
                  stroke="rgba(0,0,0,0.55)"
                  strokeWidth="0.35"
                  paintOrder="stroke"
                  fontSize="1.8"
                  fontWeight="bold"
                  className="pointer-events-none select-none"
                >
                  {`seam ${c.at}"`}
                </text>
              </g>
            );
          })}
      </svg>
    );
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title={`${quote.code} / layout`} />
      <main className="relative z-10 pb-28 pt-6">
        <div className="flex flex-wrap items-center gap-4">
          <Link
            to={`/quotes/${id}`}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
          >
            <ArrowLeft size={14} weight="bold" /> Back to quote
          </Link>
          <Link
            to={`/quotes/${id}?step=1`}
            data-testid="layout-back-to-drawing"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-indigo-300 hover:text-white"
          >
            <PencilRuler size={14} weight="bold" /> Back to drawing
          </Link>
        </div>
        <div className="mt-3">
          <div className="text-xs uppercase tracking-widest text-zinc-600">
            Slab layout
          </div>
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            {quote.project_name}
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Drop pieces on the slab photo, drag to match the vein, and watch the
            yield
          </p>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Link
            to={`/quotes/${id}?step=1`}
            data-testid="layout-screen-go-back"
            className="flex h-[52px] items-center gap-2 rounded-full border border-white/[0.07] bg-zinc-900/70 px-5 text-sm font-semibold text-zinc-300 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl transition-all duration-200 hover:bg-white/[0.08] hover:text-white"
          >
            <ArrowLeft size={16} weight="bold" /> Go back
          </Link>
          {layout.slabs.length > 1 && (
            <div
              className="flex flex-wrap items-center gap-1 rounded-full border border-white/[0.07] bg-zinc-900/70 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl"
              data-testid="layout-slab-tabs"
            >
              {layout.slabs.map((s, i) => (
                <button
                  key={s.id}
                  type="button"
                  data-testid={`layout-slab-tab-${i}`}
                  onClick={() => setActive(i)}
                  className={`flex h-10 items-center rounded-full px-5 text-sm font-semibold tracking-tight transition-all duration-200 ${
                    active === i
                      ? "bg-white/[0.11] text-white ring-1 ring-white/15"
                      : "text-zinc-500 hover:bg-white/[0.04] hover:text-zinc-200"
                  }`}
                >
                  Slab {i + 1}
                </button>
              ))}
              <span className="mx-1 h-6 w-px bg-white/10" />
              {[
                ["single", "Single slab"],
                ["all", "All slabs"],
                ["bookmatch", "Bookmatch"],
              ].map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  data-testid={`layout-view-${v}`}
                  onClick={() => setView(v)}
                  className={`flex h-10 items-center rounded-full px-5 text-sm font-semibold tracking-tight transition-all duration-200 ${
                    view === v
                      ? "bg-white/[0.11] text-white ring-1 ring-white/15"
                      : "text-zinc-500 hover:bg-white/[0.04] hover:text-zinc-200"
                  }`}
                >
                  {label}
                </button>
              ))}
              {view === "bookmatch" && (
                <button
                  type="button"
                  data-testid="mirror-toggle"
                  onClick={() => setMirror(!mirror)}
                  className={`flex h-10 items-center rounded-full px-5 text-sm font-semibold tracking-tight transition-all duration-200 ${
                    mirror
                      ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/25"
                      : "text-zinc-500 hover:bg-white/[0.04] hover:text-zinc-200"
                  }`}
                >
                  {mirror ? "Mirrored" : "Sequential"}
                </button>
              )}
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1 rounded-full border border-white/[0.07] bg-zinc-900/70 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl">
            <button
              type="button"
              data-testid="layout-tool-move"
              onClick={() => setTool("move")}
              className={`flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-all duration-200 ${
                tool === "move"
                  ? "bg-white/[0.11] text-white ring-1 ring-white/15"
                  : "text-zinc-500 hover:bg-white/[0.06] hover:text-white"
              }`}
            >
              <ArrowsOutCardinal size={16} weight="bold" /> Move
            </button>
            <button
              type="button"
              data-testid="layout-tool-seam"
              onClick={() => setTool("seam")}
              className={`flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-all duration-200 ${
                tool === "seam"
                  ? "bg-amber-500/15 text-amber-300 ring-1 ring-amber-400/25"
                  : "text-zinc-500 hover:bg-white/[0.06] hover:text-white"
              }`}
            >
              <Scissors size={16} weight="bold" /> Seam
            </button>
            {tool === "seam" && (
              <button
                type="button"
                data-testid="layout-seam-dir"
                onClick={() => setSeamDir((d) => (d === "v" ? "h" : "v"))}
                className="flex h-10 items-center rounded-full px-4 text-sm font-semibold text-zinc-300 transition-all duration-200 hover:bg-white/[0.06]"
              >
                {seamDir === "v" ? "Vertical" : "Horizontal"}
              </button>
            )}
          </div>

          {selSeam &&
            (() => {
              const c = seams.find((x) => x.id === selSeam);
              if (!c) return null;
              return (
                <div
                  data-testid="seam-edit-bar"
                  className="flex flex-wrap items-center gap-2 rounded-full border border-amber-500/40 bg-amber-500/[0.07] p-1.5 pl-4 backdrop-blur-xl"
                >
                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
                    Seam {c.dir === "v" ? "down" : "across"}
                  </span>
                  <button
                    type="button"
                    data-testid="seam-nudge-minus"
                    onClick={() => setSeamAt(c.id, c.at - 0.5)}
                    className="h-9 rounded-full px-3 font-mono-data text-xs text-zinc-300 hover:bg-white/[0.08]"
                  >
                    −½"
                  </button>
                  <input
                    type="number"
                    step="0.5"
                    data-testid="seam-at-input"
                    value={c.at}
                    onChange={(e) => setSeamAt(c.id, e.target.value)}
                    className="font-mono-data h-9 w-24 rounded-full border border-zinc-700 bg-zinc-950 px-3 text-xs text-zinc-100 outline-none focus:border-amber-400"
                  />
                  <button
                    type="button"
                    data-testid="seam-nudge-plus"
                    onClick={() => setSeamAt(c.id, c.at + 0.5)}
                    className="h-9 rounded-full px-3 font-mono-data text-xs text-zinc-300 hover:bg-white/[0.08]"
                  >
                    +½"
                  </button>
                  <button
                    type="button"
                    data-testid="seam-delete"
                    onClick={() => dropSeam(c.id)}
                    className="flex h-9 items-center gap-1.5 rounded-full border border-red-500/40 bg-red-500/10 px-4 text-xs font-semibold text-red-300 transition-all duration-200 hover:bg-red-500/20"
                  >
                    <Trash size={14} weight="bold" /> Remove seam
                  </button>
                  <button
                    type="button"
                    data-testid="seam-deselect"
                    onClick={() => setSelSeam(null)}
                    className="h-9 rounded-full px-4 text-xs font-semibold text-zinc-400 hover:bg-white/[0.06] hover:text-white"
                  >
                    Done
                  </button>
                </div>
              );
            })()}

          <div className="flex flex-wrap items-center gap-1 rounded-full border border-white/[0.07] bg-zinc-900/70 p-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl">
            <button
              type="button"
              data-testid="add-layout-slab"
              onClick={addSlab}
              className="flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-zinc-400 transition-all duration-200 hover:bg-white/[0.06] hover:text-white"
            >
              <ImageIcon size={16} weight="bold" /> Add slab
            </button>
            <button
              type="button"
              data-testid="auto-nest-button"
              disabled={!slab}
              onClick={autoNest}
              className="flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold text-indigo-300 transition-all duration-200 hover:bg-indigo-500/15 disabled:opacity-40"
            >
              <Sparkle size={16} weight="bold" /> Auto-nest
            </button>
            <button
              type="button"
              data-testid="save-layout-button"
              disabled={busy}
              onClick={save}
              className="flex h-10 items-center gap-2 rounded-full bg-white/[0.11] px-5 text-sm font-semibold text-white ring-1 ring-white/15 transition-all duration-200 hover:bg-white/[0.16] disabled:opacity-40"
            >
              <FloppyDisk size={16} weight="bold" />
              {busy ? "Saving…" : "Save"}
            </button>
          </div>

          <Link
            to={`/quotes/${id}/vein`}
            data-testid="layout-screen-vein-match"
            className="ml-auto flex h-[52px] items-center gap-2 rounded-full border border-cyan-400/25 bg-cyan-500/10 px-5 text-sm font-semibold text-cyan-200 shadow-[inset_0_1px_0_rgba(255,255,255,0.06),0_18px_40px_-20px_rgba(0,0,0,0.95)] backdrop-blur-xl transition-all duration-200 hover:bg-cyan-500/20 hover:text-white"
          >
            <ImageIcon size={16} weight="bold" /> Go to vein match
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>

        {!slab ? (
          <div className="mt-6 rounded-lg border border-dashed border-zinc-800 p-10 text-center">
            <p className="text-sm text-zinc-400">
              Add a slab to start laying out pieces.
            </p>
          </div>
        ) : (
          <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_320px]">
            <div>
              <div className="grid gap-2 sm:grid-cols-4">
                <SearchSelect
                  testId="layout-slab-select"
                  className="sm:col-span-2"
                  value={slab.slab_id || ""}
                  options={stockOptions}
                  placeholder="Search slabs or remnants…"
                  searchPlaceholder="Color, code or size…"
                  emptyText="Nothing in stock matches that"
                  onChange={pickStock}
                />
                <Input
                  data-testid="layout-slab-length"
                  type="number"
                  value={slab.length_in}
                  onChange={(e) =>
                    setSlab(active, { length_in: Number(e.target.value) })
                  }
                  className="h-11 border-zinc-800 bg-zinc-900"
                />
                <Input
                  data-testid="layout-slab-width"
                  type="number"
                  value={slab.width_in}
                  onChange={(e) =>
                    setSlab(active, { width_in: Number(e.target.value) })
                  }
                  className="h-11 border-zinc-800 bg-zinc-900"
                />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <Input
                  data-testid="layout-photo-url"
                  value={slab.photo_url}
                  onChange={(e) =>
                    setSlab(active, { photo_url: e.target.value })
                  }
                  placeholder="…or paste a slab photo URL"
                  className="h-11 min-w-[220px] flex-1 border-zinc-800 bg-zinc-900"
                />
                <Button
                  data-testid="upload-layout-photo-button"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => photoRef.current?.click()}
                  className="h-11 border-zinc-800 bg-zinc-900 text-xs uppercase tracking-widest hover:bg-zinc-800"
                >
                  <UploadSimple size={15} weight="bold" className="mr-2" />
                  {uploading ? "Uploading…" : "Upload photo"}
                </Button>
                <input
                  ref={photoRef}
                  data-testid="layout-photo-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={uploadPhoto}
                />
              </div>

              <div
                className="relative mt-4"
                onPointerMove={onPointerMove}
                onPointerUp={() => setDrag(null)}
                onPointerLeave={() => setDrag(null)}
              >
                {view === "bookmatch" && pair.length === 2 ? (
                  <div
                    className="flex items-stretch gap-0"
                    data-testid="bookmatch-view"
                  >
                    {pair.map((si, n) => (
                      <div
                        key={layout.slabs[si].id}
                        className={`relative flex-1 overflow-hidden border border-zinc-800 bg-zinc-950 ${
                          n === 0 ? "border-r-0" : "border-l-0"
                        }`}
                      >
                        <SlabCanvas
                          index={si}
                          mirrored={n === 1 && !!mirror}
                          testid={`bookmatch-canvas-${n}`}
                        />
                        {renderPieceMenu(si)}
                      </div>
                    ))}
                    <div
                      data-testid="bookmatch-seam"
                      className="pointer-events-none absolute inset-y-0 left-1/2 z-10 w-px -translate-x-1/2 bg-indigo-400"
                    >
                      <span className="absolute left-1/2 top-2 -translate-x-1/2 whitespace-nowrap rounded-full border border-indigo-400/50 bg-zinc-950/90 px-2 py-0.5 text-[9px] uppercase tracking-widest text-indigo-300">
                        Seam
                      </span>
                    </div>
                  </div>
                ) : view === "all" && layout.slabs.length > 1 ? (
                  <div
                    className="flex flex-wrap items-start gap-3"
                    data-testid="all-slabs-view"
                  >
                    {layout.slabs.map((s, i) => (
                      <div
                        key={s.id || i}
                        data-testid={`all-slab-${i}`}
                        onPointerMove={(e) => dragOntoSlab(e, i)}
                        onClick={() => setActive(i)}
                        className={`min-w-[320px] flex-1 border bg-zinc-950 ${
                          active === i
                            ? "border-indigo-400"
                            : "border-zinc-800 hover:border-zinc-600"
                        }`}
                      >
                        <div className="flex items-center gap-2 border-b border-zinc-800 px-2 py-1.5">
                          <span className="text-xs font-semibold text-zinc-200">
                            Slab {i + 1}
                          </span>
                          <span className="truncate text-[11px] text-zinc-500">
                            {s.material || "Unassigned"}
                          </span>
                          <span className="font-mono-data ml-auto text-[10px] text-zinc-500">
                            {s.length_in}" x {s.width_in}"
                          </span>
                        </div>
                        <div className="relative overflow-hidden">
                          <SlabCanvas index={i} testid={`all-canvas-${i}`} />
                          {renderPieceMenu(i)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="relative overflow-hidden border border-zinc-800 bg-zinc-950">
                    <SlabCanvas index={active} testid="layout-canvas" />
                    {renderPieceMenu(active)}
                  </div>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <p className="text-xs text-zinc-500">
                  {view === "bookmatch"
                    ? "Line up the vein across the seam — drag pieces on either slab, double-click to rotate"
                    : view === "all"
                      ? "Drag a piece from one slab straight onto another to move it · right-click a piece for more"
                      : "Drag a piece to move · double-click to rotate 90° · right-click a piece for more"}
                </p>
                {layout.slabs.length > 1 && (
                  <button
                    type="button"
                    data-testid="remove-layout-slab"
                    onClick={removeSlab}
                    className="ml-auto flex items-center gap-1.5 rounded-full border border-zinc-700 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-red-400 transition-colors duration-150 hover:bg-zinc-800"
                  >
                    <Trash size={13} weight="bold" /> Remove slab {active + 1}
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div
                className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
                data-testid="layout-yield"
              >
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Yield on this slab
                </div>
                <div className="font-mono-data mt-2 text-2xl font-bold text-indigo-300">
                  {yieldPct}%
                </div>
                <div className="mt-1 text-xs text-zinc-400">
                  {usedSqft.toFixed(1)} ft² used of {slabSqft.toFixed(1)} ft² ·{" "}
                  <span className="text-emerald-400">
                    {(slabSqft - usedSqft).toFixed(1)} ft² offcut
                  </span>
                </div>
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-zinc-800">
                  <div
                    className={`h-full rounded-full ${yieldPct > 100 ? "bg-red-500" : "bg-indigo-500"}`}
                    style={{ width: `${Math.min(yieldPct, 100)}%` }}
                  />
                </div>
              </div>

              <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Pieces
                </div>
                <div className="mt-3 space-y-2">
                  {pieces.length === 0 && (
                    <p className="text-xs text-zinc-500">
                      Add pieces on the quote first.
                    </p>
                  )}
                  {pieces.map((p) => {
                    const pl = layout.placements[p.id];
                    return (
                      <div
                        key={p.id}
                        data-testid={`layout-piece-row-${p.id}`}
                        className="flex items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-950 p-2.5"
                      >
                        <div className="min-w-0">
                          <div className="truncate text-xs font-semibold">
                            {p.label}
                          </div>
                          <div className="font-mono-data text-[10px] text-zinc-500">
                            {p.area} · {p.sqft.toFixed(1)} ft²
                          </div>
                        </div>
                        {pl ? (
                          <div className="flex shrink-0 gap-1">
                            <button
                              data-testid={`rotate-piece-${p.id}`}
                              onClick={() => rotate(p.id)}
                              className="rounded-full border border-zinc-700 p-1.5 text-zinc-400 hover:text-white"
                            >
                              <ArrowsClockwise size={12} weight="bold" />
                            </button>
                            <button
                              data-testid={`unplace-piece-${p.id}`}
                              onClick={() => unplace(p.id)}
                              className="rounded-full border border-zinc-700 px-2 py-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white"
                            >
                              Remove
                            </button>
                          </div>
                        ) : (
                          <button
                            data-testid={`place-piece-${p.id}`}
                            onClick={() => place(p.id, active)}
                            className="shrink-0 rounded-full border border-indigo-400/40 bg-indigo-500/10 px-3 py-1 text-[10px] uppercase tracking-widest text-indigo-300 hover:bg-indigo-500/20"
                          >
                            Place
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
